// The inverse of mexican-states-display: a Mexican state written any of the ways it reaches us --
// Google's `N.L.`, a TMS's `NL`, a human's `Nuevo León` -- reduced to the ISO 3166-2 code the
// server stores and matches on.
//
// This exists because Google's administrative_area_level_1 short_name is punctuated (`N.L.`,
// `Tamps.`, `B.C.S.`) and mercury rejects any state that is not alphanumeric, so the raw short_name
// 422s every pricing and recent-transactions call for a Mexican lane. Canonicalising at the point
// the value enters the app keeps the punctuation from ever reaching an API.
//
// This table mirrors MexicanStateUtils.canonicalize in cargochief-commons, which in turn agrees
// with mercury's config/mexican_states.json and MEXICAN_STATE_ALIASES. All three have to match: a
// state canonicalised one way here and another way on the server reintroduces the mismatch the
// canonical storage exists to remove.

// Canonical code -> the spellings that mean it, keyed after SUBDIVISION_LOOKUP_KEY folding. Names are listed
// unaccented and unpunctuated because the folding strips both, so one entry covers `Nuevo León`,
// `Nuevo Leon`, `N.L.` and `NL` alike.
const ALIASES: Readonly<Record<string, readonly string[]>> = Object.freeze({
	AGU: ['Aguascalientes', 'Ags'],
	BCN: ['Baja California', 'BC'],
	BCS: ['Baja California Sur'],
	CAM: ['Campeche', 'Camp'],
	CHP: ['Chiapas', 'Chis'],
	CHH: ['Chihuahua', 'Chih'],
	// Distrito Federal was renamed in 2016; TMS data still carries the old code.
	CMX: ['Ciudad de Mexico', 'CDMX', 'DF'],
	COA: ['Coahuila', 'Coah', 'Coahuila de Zaragoza'],
	COL: ['Colima'],
	DUR: ['Durango', 'Dgo'],
	GUA: ['Guanajuato', 'Gto'],
	GRO: ['Guerrero'],
	HID: ['Hidalgo', 'Hgo'],
	JAL: ['Jalisco'],
	// The state of Mexico, distinct from the country and from Mexico City above.
	MEX: ['Mexico', 'Estado de Mexico', 'Edomex'],
	MIC: ['Michoacan', 'Mich', 'Michoacan de Ocampo'],
	MOR: ['Morelos'],
	NAY: ['Nayarit'],
	NLE: ['Nuevo Leon', 'NL'],
	OAX: ['Oaxaca'],
	PUE: ['Puebla'],
	QUE: ['Queretaro', 'Qro'],
	ROO: ['Quintana Roo', 'QR', 'QRoo'],
	SLP: ['San Luis Potosi'],
	SIN: ['Sinaloa'],
	SON: ['Sonora'],
	TAB: ['Tabasco'],
	TAM: ['Tamaulipas', 'Tamps'],
	TLA: ['Tlaxcala', 'Tlax'],
	VER: ['Veracruz', 'Veracruz de Ignacio de la Llave'],
	YUC: ['Yucatan'],
	ZAC: ['Zacatecas'],
});

/**
 * Reduces a state to the form the alias table is keyed on: diacritics folded, everything that is
 * not a letter or digit dropped, upper-cased. That collapses `N.L.`, `NL`, `Nuevo Leon` and the
 * accented `Nuevo León` onto one key. Empty string when there is nothing to look up.
 */
export const SUBDIVISION_LOOKUP_KEY = (state: string): string =>
	String(state)
		.normalize('NFKD')
		.replace(/\p{M}/gu, '')
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, '');

const CANONICAL_BY_ALIAS: Readonly<Record<string, string>> = Object.freeze(
	Object.entries(ALIASES).reduce((table, [canonical, spellings]) => {
		table[SUBDIVISION_LOOKUP_KEY(canonical)] = canonical;
		spellings.forEach((spelling) => {
			table[SUBDIVISION_LOOKUP_KEY(spelling)] = canonical;
		});
		return table;
	}, {} as Record<string, string>)
);

// Folded through SUBDIVISION_LOOKUP_KEY like the states are, so the accented `México` half the sources write
// lands on the same entry. Mirrors the Mexico aliases in commons' CountryUtils.
const MEXICO_COUNTRY_KEYS = new Set(
	['MX', 'MEX', 'Mexico', 'United Mexican States', 'Estados Unidos Mexicanos'].map(SUBDIVISION_LOOKUP_KEY)
);

/**
 * Returns the ISO 3166-2 code for a Mexican state, or the value exactly as given when it is not one
 * we recognise.
 *
 * The country decides whether this does anything at all, and it has to: `BC` and `NL` each name a
 * state in both countries -- Baja California and British Columbia, Nuevo León and Newfoundland and
 * Labrador. A canonicaliser working from the subdivision alone would rewrite every British Columbia
 * lane as Mexican. So anything whose country is not Mexico is returned untouched, which also makes
 * this a no-op for every US and Canadian caller.
 *
 * A country we cannot read is treated as not-Mexico for the same reason. Leaving a value alone
 * costs a Mexican lane a match until its country is known; guessing corrupts a Canadian one
 * outright.
 */
export const CANONICALIZE_STATE = (
	state?: string | null,
	country?: string | null
): string | null | undefined => {
	if (state === null || state === undefined) {
		return state;
	}

	if (!MEXICO_COUNTRY_KEYS.has(SUBDIVISION_LOOKUP_KEY(String(country ?? '')))) {
		return state;
	}

	const key = SUBDIVISION_LOOKUP_KEY(state);
	if (!key) {
		return state;
	}

	return CANONICAL_BY_ALIAS[key] ?? state;
};

/**
 * Whether a code or name is one of Mexico's 32 federal entities. `BC` and `NL` are Canadian too, so
 * a caller that must tell them apart needs a country, not this.
 */
export const IS_MEXICAN_STATE = (state?: string | null): boolean => {
	if (state === null || state === undefined) {
		return false;
	}
	const key = SUBDIVISION_LOOKUP_KEY(state);
	return key.length > 0 && key in CANONICAL_BY_ALIAS;
};

export { CANONICAL_BY_ALIAS as MEXICAN_STATE_CANONICAL_BY_ALIAS };
